import { Suspense, useEffect, useRef } from 'react';

import { CALL_SCRIPT_STEP_KEY_VOICEMAIL } from '@components/callScript/constants';
import { FloatingCallScript } from '@components/callScript/FloatingCallScript';
import { LoadingCallScript } from '@components/callScript/LoadingCallScript';
import { useSuspenseQueryCallScript } from '@hooks/query/callScripts/useSuspenseQueryCallScript';
import { useQueryCallDetails } from '@hooks/query/useQueryCallDetails';
import { CallDirectionEnum } from '@lib/enums';
import {
  callScriptPanelAtom,
  INITIAL_CALL_SCRIPT_PANEL_STATE,
} from '@states/callScript/callScriptPanelAtom';
import { useJotaiValue, useSetJotaiAtom } from '@states/jotai/hooks';
import { contactSelector } from '@states/softphone/contactSelector';

import type { TContact } from '@states/softphone/contactSelector';
import type { FC } from 'react';

export const CallScriptLauncher: FC = () => {
  const contact = useJotaiValue(contactSelector);
  const setCallScriptPanelState = useSetJotaiAtom(callScriptPanelAtom);

  const shouldFetchCallDetails =
    contact.isActiveContact &&
    contact.callDirection === CallDirectionEnum.Outbound &&
    contact.isWelcomeWagon === true &&
    contact.contactId != null &&
    contact.channel != null;

  // CallScriptLauncher stays mounted for the lifetime of the app (unlike its children below,
  // which unmount the instant the call ends), so it's the only place that can reliably reset
  // the panel once the call is no longer eligible for a script.
  useEffect(() => {
    if (!shouldFetchCallDetails) {
      setCallScriptPanelState(INITIAL_CALL_SCRIPT_PANEL_STATE);
    }
  }, [shouldFetchCallDetails, setCallScriptPanelState]);

  if (!shouldFetchCallDetails) {
    return null;
  }

  return <CallScriptDetails contact={contact} />;
};

type CallScriptDetailsProps = Readonly<{
  contact: TContact;
}>;

const CallScriptDetails: FC<CallScriptDetailsProps> = ({ contact }) => {
  const setCallScriptPanelState = useSetJotaiAtom(callScriptPanelAtom);

  const { data: callDetails, isLoading } = useQueryCallDetails({
    contactMasterId: contact.contactMasterId,
    leadId: contact.leadId,
    transferQuoteId: contact.transferQuoteId,
  });

  const scriptKey = callDetails?.scriptKey;

  useEffect(() => {
    if (!isLoading && scriptKey == null) {
      setCallScriptPanelState(INITIAL_CALL_SCRIPT_PANEL_STATE);
    }
  }, [isLoading, scriptKey, setCallScriptPanelState]);

  if (isLoading || scriptKey == null) {
    return null;
  }

  return (
    <Suspense fallback={<LoadingCallScript />}>
      <CallScriptSteps scriptKey={scriptKey} contractNumber={callDetails?.contractNumber} />
    </Suspense>
  );
};

type CallScriptStepsProps = Readonly<{
  scriptKey: string;
  contractNumber: Maybe<string>;
}>;

const CallScriptSteps: FC<CallScriptStepsProps> = ({ scriptKey, contractNumber }) => {
  const setCallScriptPanelState = useSetJotaiAtom(callScriptPanelAtom);
  const wasScriptAvailableRef = useRef(false);

  const { data: callScript } = useSuspenseQueryCallScript(scriptKey, contractNumber);
  const hasScript = callScript?.steps.length > 0;

  useEffect(() => {
    if (hasScript && !wasScriptAvailableRef.current) {
      setCallScriptPanelState({ isAvailable: true, isOpen: true });
    } else if (!hasScript && wasScriptAvailableRef.current) {
      setCallScriptPanelState(INITIAL_CALL_SCRIPT_PANEL_STATE);
    }

    wasScriptAvailableRef.current = hasScript;
  }, [hasScript, setCallScriptPanelState]);

  if (!hasScript) {
    return null;
  }

  return (
    <FloatingCallScript
      title={callScript.title ?? 'Call Script'}
      steps={callScript.steps.filter((step) => step.stepKey !== CALL_SCRIPT_STEP_KEY_VOICEMAIL)}
    />
  );
};
